import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Loader2, ListTodo, RefreshCw, Unlink } from 'lucide-react';

// Settings card: connect Microsoft To Do so assigned tasks appear in the user's "OLTA Deals" list.
export function MsTodoCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.mstodoStatus().then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const res = params.get('mstodo');
    if (res === 'connected') {
      toast.success('Microsoft To Do connected — your tasks are syncing now');
      window.history.replaceState({}, '', '/settings');
    } else if (res === 'error') {
      toast.error(`Microsoft To Do connection failed (${params.get('reason') || 'unknown'})`);
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.mstodoConnect();
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start Microsoft login');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.mstodoDisconnect();
      toast.success('Microsoft To Do disconnected');
      load();
    } catch {
      toast.error('Could not disconnect');
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.mstodoSync();
      toast.success(`Sync complete — ${r.data.synced} change${r.data.synced === 1 ? '' : 's'} pushed`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  if (!status || !status.configured) return null;

  return (
    <div className="bg-card border border-border rounded-sm p-5 mt-6" data-testid="mstodo-card">
      <div className="flex items-center gap-2 mb-1">
        <ListTodo className="w-4 h-4 text-blue-400" />
        <h2 className="font-heading text-lg text-foreground">Microsoft To Do & E-Mail</h2>
        {status.connected && (
          <span className="text-[10px] uppercase tracking-wide border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 rounded-sm px-1.5 py-0.5 font-mono">Connected</span>
        )}
        {status.connected && status.realtime && (
          <span className="text-[10px] uppercase tracking-wide border border-blue-500/30 bg-blue-500/10 text-blue-300 rounded-sm px-1.5 py-0.5 font-mono" data-testid="mstodo-realtime-badge">Real-time</span>
        )}
        {status.connected && (status.mail_ready ? (
          <span className="text-[10px] uppercase tracking-wide border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 rounded-sm px-1.5 py-0.5 font-mono" data-testid="mstodo-mail-ready-badge">E-Mail aktiv</span>
        ) : (
          <span className="text-[10px] uppercase tracking-wide border border-amber-500/30 bg-amber-500/10 text-amber-300 rounded-sm px-1.5 py-0.5 font-mono" data-testid="mstodo-mail-missing-badge">E-Mail fehlt</span>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Everything actionable lands in an <span className="text-foreground">"OLTA Deals"</span> list in Microsoft To Do:
        your tasks, pending reviews, deal check-ins, SLA alerts and "dragging along" deals — each with a deal link.
        Ticking a task off in To Do completes it here in near real-time (incl. the workflow step); items that need a
        decision in the platform re-appear with a {'\u26a0\ufe0f'} warning and a push notification.
        The same connection also enables <span className="text-foreground">automatic e-mail sending</span> from your
        mailbox (sequences &amp; broker replies).
      </p>
      {status.connected && !status.mail_ready && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 mb-4" data-testid="mstodo-mail-warning">
          <p className="text-[11px] text-amber-300">
            Dein Microsoft-Konto ist verbunden, aber ohne E-Mail-Berechtigung — Sequenz-E-Mails werden dir als
            manuelle Aufgaben angelegt statt automatisch versendet. Einmal neu verbinden und die E-Mail-Berechtigung
            bestätigen, dann läuft der Versand automatisch.
          </p>
          <Button onClick={connect} disabled={busy} size="sm" data-testid="mstodo-reconnect-btn"
            className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-7 px-3 mt-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ListTodo className="w-3.5 h-3.5 mr-1" />}
            Neu verbinden &amp; E-Mail aktivieren
          </Button>
        </div>
      )}
      {status.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-xs text-muted-foreground mr-auto">
            <span className="text-foreground">{status.account}</span>
            <span className="ml-3">{status.synced_tasks} task{status.synced_tasks === 1 ? '' : 's'} in To Do</span>
          </div>
          <Button onClick={syncNow} disabled={syncing} size="sm" variant="outline"
            className="border-border rounded-sm text-xs h-8" data-testid="mstodo-sync-btn">
            {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Sync now
          </Button>
          <Button onClick={disconnect} disabled={busy} size="sm" variant="ghost"
            className="text-xs h-8 text-muted-foreground hover:text-red-400" data-testid="mstodo-disconnect-btn">
            <Unlink className="w-3.5 h-3.5 mr-1" /> Disconnect
          </Button>
        </div>
      ) : (
        <Button onClick={connect} disabled={busy} size="sm"
          className="bg-blue-700 hover:bg-blue-600 text-white rounded-sm text-xs h-8 px-4" data-testid="mstodo-connect-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ListTodo className="w-3.5 h-3.5 mr-1" />}
          Connect Microsoft To Do
        </Button>
      )}
    </div>
  );
}
